<script setup lang="ts">
import { ref, computed, watch, inject, onUnmounted } from "vue";
import { useSidebarStore } from "@/stores/sidebar";
import { generateSentenceByType } from "@/sentences/generator";
import { generateNPCAppearance } from "@/sentences/npc-appearance";
import type { ITableStore } from "@/table-store";
import {
  CONTENT_SOURCE_KEY,
  coversSetting,
  generatorOptions,
  slugify,
  type ContentSource,
} from "@/content";
import ResultBox from "./ResultBox.vue";
import TagTallyIcon from "./TagTallyIcon.vue";

const store = useSidebarStore();
const tableStore = inject<ITableStore | null>("tableStore", null);
/**
 * Generators from the host (the plugin's vault folders). When present and it
 * covers the chosen setting, the tab lists and runs those; otherwise it uses
 * the bundled sentence code below, as the web app always does.
 */
const source = inject<ContentSource | null>(CONTENT_SOURCE_KEY, null);
const contentVersion = ref(0);
if (source) onUnmounted(source.onChange(() => contentVersion.value++));

const SENTENCE_TYPES = [
  "Scene", "Creature", "Quest", "NPC/Character", "Species", "Type", "Wounds",
  "Blood Magic Trauma", "Faction", "Location", "Dungeon", "Lair", "Room",
  "Armor", "Item", "Weapon", "Trap", "Consumable", "Resource", "Insight",
  "Appearance",
];

const WOUND_INJURY_TYPES = ["Strain", "Lingering Trauma", "Debilitating Injury", "Lasting Scar"];

const THEMES = [
  { value: "fantasy", label: "Fantasy" },
  { value: "modern", label: "Modern" },
  { value: "monster-hunter", label: "Monster Hunter" },
];

/** Old sentence names that differ from generator names; also maps generator names back. */
const ALIASES: Record<string, string> = { "npc-character": "npc", character: "npc", wound: "wounds" };
const typeKey = (t: string) => ALIASES[slugify(t)] ?? slugify(t);

/** Shown under a generator's result in this tab, as the NPC tab always showed appearance. */
const APPENDIX: Record<string, string> = { npc: "appearance" };

const theme = ref(store.sentencesTheme);
const selectedSentenceType = ref(store.selectedSentenceType);
const useFantasySpecies = ref(store.useFantasySpecies);

const creatureType = ref("");
const injuryType = ref("");
const damageType = ref("");
const itemType = ref("");
/** Vault mode: chosen value per generator option; "" rolls it. */
const optionValues = ref<Record<string, string>>({});

const generatedText = ref("");
const appearanceText = ref("");

watch(theme, (v) => { store.sentencesTheme = v; });
watch(selectedSentenceType, (v) => { store.selectedSentenceType = v; });
watch(useFantasySpecies, (v) => { store.useFantasySpecies = v; });

const vaultGenerators = computed(() => {
  void contentVersion.value;
  return source ? source.registry.list("generator", theme.value) : [];
});
const useVault = computed(() => {
  void contentVersion.value;
  return !!source && vaultGenerators.value.length > 0 && coversSetting(source, theme.value);
});

const themeOptions = computed(() => {
  void contentVersion.value;
  const extra = (source?.registry.settings() ?? [])
    .filter((s) => !THEMES.some((t) => t.value === s))
    .map((s) => ({ value: s, label: s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) }));
  return [...THEMES, ...extra];
});

const typeOptions = computed(() =>
  useVault.value
    ? vaultGenerators.value.map((g) => ({ value: g.name, label: g.label }))
    : SENTENCE_TYPES.map((t) => ({ value: t, label: t })),
);

/** The saved type in whichever list is showing ("NPC/Character" and "npc" are the same). */
const currentType = computed(() => {
  const key = typeKey(selectedSentenceType.value);
  return typeOptions.value.find((o) => typeKey(o.value) === key)?.value ?? typeOptions.value[0]?.value ?? "";
});

const vaultOptions = computed(() => {
  void contentVersion.value;
  return useVault.value && source ? generatorOptions(source, currentType.value, theme.value) : [];
});
watch(currentType, () => { optionValues.value = {}; });

function getTableValues(tableName: string): string[] {
  if (!tableStore) return [];
  return tableStore.getTableValues(tableName);
}

const creatureTypeOptions = computed(() => getTableValues("creature-type"));
const itemTypeOptions = computed(() => getTableValues("item-type"));
const damageTypeOptions = computed(() => getTableValues("wound-damage"));

const showCreatureType = computed(() => !useVault.value && currentType.value === "Creature");
const showItemType = computed(() => !useVault.value && currentType.value === "Item");
const showWoundsOptions = computed(() => !useVault.value && currentType.value === "Wounds");
const showUseFantasySpecies = computed(() =>
  !useVault.value &&
  currentType.value === "NPC/Character" &&
  (theme.value === "modern" || theme.value === "monster-hunter")
);

function generateFromVault(): void {
  if (!source) return;
  const params = Object.fromEntries(Object.entries(optionValues.value).filter(([, v]) => v));
  const name = currentType.value;
  generatedText.value = source.engine.generate(name, { setting: theme.value, params }).text;
  const appendix = APPENDIX[name];
  appearanceText.value =
    appendix && source.registry.getGenerator(appendix, theme.value)
      ? source.engine.generate(appendix, { setting: theme.value }).text
      : "";
}

function generateBundled(): void {
  generatedText.value = generateSentenceByType(currentType.value, {
    theme: theme.value,
    useFantasySpecies: useFantasySpecies.value,
    creatureType: creatureType.value || undefined,
    itemType: itemType.value || undefined,
    injuryType: injuryType.value || undefined,
    damageType: damageType.value || undefined,
  });
  if (currentType.value === "NPC/Character" && tableStore?.hasTable("character-build-height")) {
    try {
      appearanceText.value = generateNPCAppearance(tableStore);
    } catch {
      appearanceText.value = "";
    }
  } else {
    appearanceText.value = "";
  }
}

function updateGenerated() {
  if (useVault.value) generateFromVault();
  else generateBundled();
}

/** Vault text is user-editable, so escape it before adding the bold and line breaks. */
function toHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*([^*]*)\*\*/g, "<strong>$1</strong>")
    .replace(/\n/g, "<br>");
}

const generatedHtml = computed(() => {
  const sentenceHtml = toHtml(generatedText.value);
  return appearanceText.value ? `${sentenceHtml}<br><br>${toHtml(appearanceText.value)}` : sentenceHtml;
});

watch(
  [theme, currentType, useFantasySpecies, creatureType, injuryType, damageType, itemType, optionValues, contentVersion],
  updateGenerated,
  { immediate: true, deep: true },
);

function reroll() {
  updateGenerated();
}

const sentencesCopyText = computed(() =>
  appearanceText.value ? `${generatedText.value}\n\n${appearanceText.value}` : generatedText.value,
);
</script>

<template>
  <div class="tag-tally-sentences-vue-wrapper">
    <div class="tag-tally-theme-container tag-tally-select-container">
      <label for="sentences-theme-select">Setting</label>
      <select id="sentences-theme-select" v-model="theme" class="tag-tally-select">
        <option v-for="opt in themeOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
    </div>

    <div class="tag-tally-sentence-type-container tag-tally-select-container">
      <label for="sentence-type-select">{{ useVault ? "Generator" : "Sentence Type" }}</label>
      <div class="tag-tally-sentence-type-row">
        <select
          id="sentence-type-select"
          :value="currentType"
          class="tag-tally-select"
          @change="selectedSentenceType = ($event.target as HTMLSelectElement).value"
        >
          <option v-for="t in typeOptions" :key="t.value" :value="t.value">
            {{ t.label }}
          </option>
        </select>
        <button type="button" class="clickable-icon" aria-label="Re-roll sentence" @click="reroll">
          <TagTallyIcon name="dices" />
        </button>
      </div>
    </div>

    <div v-if="vaultOptions.length" class="tag-tally-wounds-options">
      <div v-for="opt in vaultOptions" :key="opt.name" class="tag-tally-select-container">
        <label :for="`generator-option-${opt.name}`">{{ opt.label }}</label>
        <select :id="`generator-option-${opt.name}`" v-model="optionValues[opt.name]" class="tag-tally-select">
          <option :value="undefined">All</option>
          <option v-for="v in opt.values" :key="v" :value="v">{{ v }}</option>
        </select>
      </div>
    </div>

    <div v-if="showCreatureType" class="tag-tally-select-container">
      <label for="creature-type-select">Creature Type</label>
      <select id="creature-type-select" v-model="creatureType" class="tag-tally-select">
        <option value="">All</option>
        <option v-for="v in creatureTypeOptions" :key="v" :value="v">{{ v }}</option>
      </select>
    </div>

    <div v-if="showItemType" class="tag-tally-select-container">
      <label for="item-type-select">Item Type</label>
      <select id="item-type-select" v-model="itemType" class="tag-tally-select">
        <option value="">All</option>
        <option v-for="v in itemTypeOptions" :key="v" :value="v">{{ v }}</option>
      </select>
    </div>

    <div v-if="showWoundsOptions" class="tag-tally-wounds-options">
      <div class="tag-tally-select-container">
        <label for="injury-type-select">Injury Type</label>
        <select id="injury-type-select" v-model="injuryType" class="tag-tally-select">
          <option value="">All</option>
          <option v-for="w in WOUND_INJURY_TYPES" :key="w" :value="w">{{ w }}</option>
        </select>
      </div>
      <div class="tag-tally-select-container">
        <label for="damage-type-select">Damage Type</label>
        <select id="damage-type-select" v-model="damageType" class="tag-tally-select">
          <option value="">All</option>
          <option v-for="v in damageTypeOptions" :key="v" :value="v">{{ v }}</option>
        </select>
      </div>
    </div>

    <div v-if="showUseFantasySpecies" class="tag-tally-checkbox-container">
      <input id="fantasy-species-checkbox" v-model="useFantasySpecies" type="checkbox" />
      <label for="fantasy-species-checkbox">Fantasy Species</label>
    </div>

    <ResultBox
      class="tag-tally-sentences-result"
      :copy-value="sentencesCopyText"
      :content="generatedHtml"
      content-mode="html"
    />
  </div>
</template>

<style scoped>
.tag-tally-sentence-type-row {
  display: flex;
  align-items: center;
  gap: var(--size-2, 0.5rem);
}
.tag-tally-sentence-type-row .tag-tally-select {
  flex: 1;
}
.tag-tally-wounds-options {
  display: flex;
  flex-wrap: wrap;
  gap: var(--size-3, 1rem);
}
.tag-tally-sentences-result {
  margin-top: var(--size-3, 1rem);
}
.tag-tally-checkbox-container {
  display: flex;
  align-items: center;
  gap: var(--size-2, 0.5rem);
  margin-bottom: var(--size-2, 0.5rem);
}
.clickable-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: var(--size-1, 0.25rem);
  min-width: var(--size-4, 1.5rem);
  min-height: var(--size-4, 1.5rem);
  border: none;
  border-radius: var(--radius-1, 2px);
  background: transparent;
  color: var(--gray-6, var(--text-muted));
  cursor: pointer;
}
.clickable-icon:hover {
  color: var(--text-normal);
  background: var(--background-modifier-hover);
}
</style>
